import * as React from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import { change } from 'redux-form';

import type { CompanyWorkDaysForm } from 'ab-requestModels/company.requestModel';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';
import * as UserUtils from 'af-utils/user.util';
import { convertWorkDays } from 'af-utils/workDay.util';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import Breadcrumbs from 'af-components/Breadcrumbs';
import LoadingIndicator from 'af-components/LoadingIndicator';
import { CompanyFormModel } from 'af-components/SharedForms/Company/BasicInfoForm/form';

import * as CompanyActions from 'af-actions/companies';

import CompanyEdit from './CompanyInfo/CompanyEdit';
import CompanyPreview from './CompanyInfo/CompanyPreview';
import CompanyPolicyEdit from './CompanyPolicy/Edit';
import { CompanyPolicyFM } from './CompanyPolicy/formModel';
import CompanyPolicyPreview from './CompanyPolicy/Preview';
import CompanyWaiverSettingsEdit from './CompanyWaiverSettings/Edit';
import CompanyWaiverSettingsPreview from './CompanyWaiverSettings/Preview';
import EHSEdit from './EHS/EHSEdit';
import EHSPreview from './EHS/EHSPreview';
import EnableInvoiceNotificationsCompanyEdit from './InvoiceNotifications/Edit';
import EnableInvoiceNotificationsPreview from './InvoiceNotifications/Preview';
import EnableJobTrackingEdit from './JobTracking/Edit';
import EnableJobTrackingPreview from './JobTracking/Preview';
import EnableMaterialsEdit from './Materials/Edit';
import EnableMaterialsPreview from './Materials/Preview';
import EnablePortalFunctionalitiesEdit from './Portal/Edit';
import EnablePortalFunctionalitiesPreview from './Portal/Preview';
import CompanyPrintoutEdit from './Printouts/Edit';
import CompanyPrintoutPreview from './Printouts/Preview';
import PurchaseOrderNumberPrefixEdit from './PurchaseOrders/Edit';
import PurchaseOrderNumberPrefixPreview from './PurchaseOrders/Preview';
import ShiftSettingsEdit from './ShiftSettings/Edit';
import { ShiftSettingsFM } from './ShiftSettings/formModel';
import ShiftSettingsPreview from './ShiftSettings/Preview';
import TimeRoundingEdit from './TimeRounding/Edit';
import TimeRoundingPreview from './TimeRounding/Preview';
import CompanyWorkDaysEdit from './WorkDaysSettings/CompanyWorkDaysEdit';
import CompanyWorkDaysPreview from './WorkDaysSettings/CompanyWorkDaysPreview';
import EnableWorkRequestCompanyEdit from './WorkRequests/Edit';
import EnableWorkRequestsCompanyPreview from './WorkRequests/Preview';

interface CompanySettingsRouteProps {
	orgAlias: string;
}

type OwnProps = CustomRouteComponentProps<CompanySettingsRouteProps>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const CompanySettings: React.FC<Props> = (props) => {
	const { company, changeField, isCompanyAdmin, arePortalFunctionalitiesEnabled, isPlatformAdmin, getCompany } = props;

	const { value: editBasicInfo, toggle: toggleBasicInfoEdit } = useToggle(false);
	const { value: editWorkDaysInfo, toggle: toggleWorkDaysEdit } = useToggle(false);
	const { value: editPrintouts, toggle: togglePrintoutsEdit } = useToggle(false);
	const { value: editTimeRounding, toggle: toggleTimeRoundingEdit } = useToggle(false);
	const { value: editEHS, toggle: toggleEHSEdit } = useToggle(false);
	const { value: editWorkRequests, toggle: toggleWorkRequestsEdit } = useToggle(false);
	const { value: editInvoiceNotifications, toggle: toggleInvoiceNotificationsEdit } = useToggle(false);
	const { value: editPurchaseOrderNumberPrefix, toggle: togglePurchaseOrderNumberPrefixEdit } = useToggle(false);
	const { value: editPortalFunctionalities, toggle: togglePortalFunctionalitiesEdit } = useToggle(false);
	const { value: editMaterials, toggle: toggleMaterialsEdit } = useToggle(false);
	const { value: editJobTracking, toggle: toggleJobTrackingEdit } = useToggle(false);
	const { value: showShiftSettings, toggle: toggleShiftSettings } = useToggle(false);
	const { value: showCompanyPolicy, toggle: toggleCompanyPolicy } = useToggle(false);
	const { value: showUsertWaiverSettings, toggle: toggleUserWaiverSettings } = useToggle(false);

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			await getCompany();
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!company) {
		return <LoadingIndicator />;
	}

	return (
		<div className="form-segment">
			<Breadcrumbs items={[{ label: 'Company' }]} />
			<CompanyPreview company={company} toggleEdit={toggleBasicInfoEdit} />
			<CompanyEdit
				{...props}
				changeField={changeField}
				company={company}
				initialValues={CompanyFormModel.fromVM({ ...company.address, ...company })}
				isVisible={editBasicInfo}
				toggleEdit={toggleBasicInfoEdit}
			/>
			<Breadcrumbs items={[{ label: 'Work Days' }]} />
			<CompanyWorkDaysPreview company={company} toggleEdit={toggleWorkDaysEdit} />
			<CompanyWorkDaysEdit
				{...props}
				company={company}
				initialValues={{ holidays: company.holidays, ...convertWorkDays(company.workDays) } as CompanyWorkDaysForm}
				isVisible={editWorkDaysInfo}
				toggleEdit={toggleWorkDaysEdit}
			/>
			{(!DisabledFeatures.TimeRounding1Min && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Time Rounding' }]} />
					<TimeRoundingPreview company={company} toggleEdit={toggleTimeRoundingEdit} />
					<TimeRoundingEdit
						closeModal={toggleTimeRoundingEdit}
						showModal={editTimeRounding}
						timeRoundingPolicyFrom={company.timeRoundingPolicyFrom}
					/>
					<Breadcrumbs items={[{ label: 'User Waiver Settings' }]} />
					<CompanyWaiverSettingsPreview
						company={company}
						toggleEdit={toggleUserWaiverSettings}
					/>
					<CompanyWaiverSettingsEdit
						closeModal={toggleUserWaiverSettings}
						companyWaiverStatus={company.companyWaiverStatus}
						showModal={showUsertWaiverSettings}
						timeRoundingPolicyFrom={company.timeRoundingPolicyFrom}
					/>
					<Breadcrumbs items={[{ label: 'Shift Settings' }]} />
					<ShiftSettingsPreview
						maximumShiftDurationBeforeBreak1={company.maximumShiftDurationBeforeBreak1}
						maximumShiftDurationBeforeBreak2={company.maximumShiftDurationBeforeBreak2}
						minimumBreakDuration={company.minimumBreakDuration}
						timeBetweenShifts={company.timeBetweenShifts}
						toggleEdit={toggleShiftSettings}
					/>
					<ShiftSettingsEdit
						closeModal={toggleShiftSettings}
						initialValues={ShiftSettingsFM.fromVM(company)}
						showModal={showShiftSettings}
					/>
					<Breadcrumbs items={[{ label: 'Company Policy' }]} />
					<CompanyPolicyPreview
						companyPolicyAttachmentName={company.companyPolicyAttachmentName}
						toggleEdit={toggleCompanyPolicy}
					/>
					<CompanyPolicyEdit
						closeModal={toggleCompanyPolicy}
						initialValues={CompanyPolicyFM.fromVM(company)}
						showModal={showCompanyPolicy}
					/>
				</>
			)}
			<Breadcrumbs items={[{ label: 'Printouts' }]} />
			<CompanyPrintoutPreview company={company} toggleEdit={togglePrintoutsEdit} />
			<CompanyPrintoutEdit
				{...props}
				isVisible={editPrintouts}
				toggleEdit={togglePrintoutsEdit}
			/>
			{(!DisabledFeatures.Compliance && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'EHS' }]} />
					<EHSPreview company={company} toggleEdit={toggleEHSEdit} />
					<EHSEdit
						{...props}
						initialValues={{ ehsUrl: company.complianceEhsUrl }}
						isVisible={editEHS}
						toggleEdit={toggleEHSEdit}
					/>
				</>
			)}
			{(!DisabledFeatures.WorkRequest && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Work Requests' }]} />
					<EnableWorkRequestsCompanyPreview areWorkRequestsEnabled={company.areWorkRequestsEnabled} toggleEdit={toggleWorkRequestsEdit} />
					<EnableWorkRequestCompanyEdit
						areWorkRequestsEnabled={company.areWorkRequestsEnabled}
						closeModal={toggleWorkRequestsEdit}
						showModal={editWorkRequests}
					/>
				</>
			)}
			{(!DisabledFeatures.Project && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Portal Functionalities' }]} />
					<EnablePortalFunctionalitiesPreview
						arePortalFunctionalitiesEnabled={company.arePortalFunctionalitiesEnabled}
						toggleEdit={togglePortalFunctionalitiesEdit}
					/>
					<EnablePortalFunctionalitiesEdit
						arePortalFunctionalitiesEnabled={company.arePortalFunctionalitiesEnabled}
						closeModal={togglePortalFunctionalitiesEdit}
						showModal={editPortalFunctionalities}
					/>
				</>
			)}
			{(!DisabledFeatures.Materials && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Materials' }]} />
					<EnableMaterialsPreview
						areMaterialsEnabled={company.areMaterialsEnabled}
						toggleEdit={toggleMaterialsEdit}
					/>
					<EnableMaterialsEdit
						areMaterialsEnabled={company.areMaterialsEnabled}
						closeModal={toggleMaterialsEdit}
						showModal={editMaterials}
					/>
				</>
			)}
			{(!DisabledFeatures.JobTracking && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Job Tracking' }]} />
					<EnableJobTrackingPreview
						isJobTrackingEnabled={company.isJobTrackingEnabled}
						syncTime={company.jobTrackingSyncTime}
						toggleEdit={toggleJobTrackingEdit}
					/>
					<EnableJobTrackingEdit
						closeModal={toggleJobTrackingEdit}
						isJobTrackingEnabled={company.isJobTrackingEnabled}
						jobTrackingSyncTime={company.jobTrackingSyncTime}
						showModal={editJobTracking}
					/>
				</>
			)}
			{(!DisabledFeatures.Invoices && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Invoices' }]} />
					<EnableInvoiceNotificationsPreview
						emailDomain={company.emailDomain}
						invoiceHeaderImageUrl={company.invoiceHeaderImageUrl}
						invoiceMessage={company.invoiceMessage}
						isPlatformAdmin={isPlatformAdmin}
						isSendingInvoiceNotificationsEnabled={company.isSendingInvoiceNotificationsEnabled}
						toggleEdit={toggleInvoiceNotificationsEdit}
					/>
					{editInvoiceNotifications &&
						<EnableInvoiceNotificationsCompanyEdit
							closeModal={toggleInvoiceNotificationsEdit}
							initialEmailDomain={company.emailDomain}
							initialImageUrl={company.invoiceHeaderImageUrl}
							initialIsSendingInvoiceNotificationsEnabled={company.isSendingInvoiceNotificationsEnabled}
							initialMessage={company.invoiceMessage}
							isPlatformAdmin={isPlatformAdmin}
						/>
					}
				</>
			)}
			{(!DisabledFeatures.PurchaseOrder && arePortalFunctionalitiesEnabled && isCompanyAdmin) && (
				<>
					<Breadcrumbs items={[{ label: 'Purchase Orders' }]} />
					<PurchaseOrderNumberPrefixPreview prefix={company.purchaseOrderNumberPrefix} toggleEdit={togglePurchaseOrderNumberPrefixEdit} />
					<PurchaseOrderNumberPrefixEdit
						closeModal={togglePurchaseOrderNumberPrefixEdit}
						currentPrefix={company.purchaseOrderNumberPrefix}
						showModal={editPurchaseOrderNumberPrefix}
					/>
				</>
			)}
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData }, company: { company } } = state;
	if (!companyData || !userData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
		company,
		isCompanyAdmin: companyData.isCompanyAdmin,
		arePortalFunctionalitiesEnabled: companyData.arePortalFunctionalitiesEnabled,
		isPlatformAdmin: UserUtils.isAdmin(userData),
	};
}

function mapDispatchToProps() {
	return {
		getCompany: CompanyActions.getCompany,
		changeField: change,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(CompanySettings);
